import { useState, type CSSProperties, type ReactNode } from 'react'
import { Alert, Button, Loader, Table } from '@mantine/core'
import {
  IconAd,
  IconChartBar,
  IconMail,
  IconMessageCircle,
  IconMessages,
} from '@tabler/icons-react'
import type { CrmLeadSource, CrmLeadStatus } from '@behuman/core-contract'
import {
  useCrmCampaign,
  useCrmLeadReport,
  useLeadStatusBreakdown,
  type LeadFunnel,
  type LeadMilestone,
  type LeadReport,
} from './hooks'
import { MessagePreview } from './CampaignsTab'
import { CrmModal, CrmSection } from './CrmModal'
import {
  CORAL,
  CountChip,
  CrmCard,
  CrmChip,
  CrmIconTile,
  CrmSearchInput,
  LeadStatusBadge,
  NameButton,
  tableHeaderClass,
} from './CrmUi'
import {
  CHANNEL_LABELS,
  CHIP_TONES,
  LEAD_MILESTONE_LABELS,
  LEAD_STATUS_COLORS,
  LEAD_STATUS_LABELS,
  LEAD_SOURCE_LABELS,
  type ChipColor,
} from './labels'

/**
 * The leads behind one report count, as the Leads view's filters. All absent is every lead.
 * - `source` (+ `campaign`): leads that came from there.
 * - `outreach`: leads an SMS/email campaign reached; `milestone` then counts since the send.
 * - `status`: of those, the leads in this status now.
 */
export interface ReportDrill {
  source?: CrmLeadSource
  campaign?: string | null
  outreach?: { id: number; name: string }
  milestone?: LeadMilestone
  status?: CrmLeadStatus
}

const percent = (part: number, whole: number) =>
  whole ? `${Math.round((part / whole) * 100)}%` : ''

/** Whether any of a row's texts contains the search, ignoring case; an empty search matches all. */
const matches = (search: string, ...texts: string[]) => {
  const needle = search.trim().toLowerCase()
  return !needle || texts.some((text) => text.toLowerCase().includes(needle))
}

const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })

const registeredLabel = LEAD_MILESTONE_LABELS.registered.toLowerCase()
const bookedLabel = LEAD_MILESTONE_LABELS.booked.toLowerCase()

/**
 * A count in a report table, right-aligned, that opens the leads behind it, zero included: dark
 * text that turns blue and underlines on hover. A rate sits under it, absent when the row has no
 * leads.
 */
function CountLink({
  count,
  label,
  rate,
  onClick,
}: {
  count: number
  label: string
  rate?: string
  onClick: () => void
}) {
  return (
    <span className="inline-flex flex-col items-end">
      <button
        type="button"
        aria-label={`View ${count} ${label}`}
        onClick={(event) => {
          // The row opens the campaign's dialog; a count opens its leads instead.
          event.stopPropagation()
          onClick()
        }}
        className="cursor-pointer rounded border-0 bg-transparent p-0 text-sm text-[#1F2733] tabular-nums underline-offset-4 hover:text-[#24548A] hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#316DAB]"
      >
        {count}
      </button>
      {rate && <span className="text-xs leading-tight text-[#6B7280] tabular-nums">{rate}</span>}
    </span>
  )
}

/**
 * One step of the leads funnel: what it counts, its figure (opening every lead that reached it)
 * and a bar for its share of all leads, in the colour of the status chip it stands for (all leads
 * a quiet grey).
 */
function FunnelStep({
  label,
  value,
  share,
  color,
  buttonLabel,
  onClick,
}: {
  label: string
  value: number
  /** 0–1 of all leads; the bar's length. */
  share: number
  color?: ChipColor
  buttonLabel: string
  onClick: () => void
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[13px] text-[#6B7280]">{label}</span>
        <button
          type="button"
          aria-label={`View ${value} ${buttonLabel}`}
          onClick={onClick}
          className="cursor-pointer rounded border-0 bg-transparent p-0 text-[28px] leading-tight font-bold text-[#1F2733] tabular-nums hover:text-[#24548A] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#316DAB]"
        >
          {value}
        </button>
      </div>
      <div
        aria-hidden
        className="h-2 overflow-hidden rounded-full"
        style={{ backgroundColor: CHIP_TONES.gray.bg }}
      >
        <div
          className="h-full rounded-full"
          style={{
            width: `${share * 100}%`,
            minWidth: share > 0 ? 8 : 0,
            backgroundColor: color ? CHIP_TONES[color].fg : '#B8BFC9',
          }}
        />
      </div>
    </div>
  )
}

/** Leads, registered and booked-or-seen cells for one group of leads. */
function FunnelCells({
  funnel,
  leadsLabel,
  of,
  open,
  edge,
}: {
  funnel: LeadFunnel
  /** What the leads count is, e.g. "new leads". */
  leadsLabel: string
  /** Where the leads came from, for the buttons' labels. */
  of: string
  open: (milestone?: LeadMilestone) => void
  /** Drops the last cell's right padding, for a table flush with its card. */
  edge?: boolean
}) {
  return (
    <>
      <Table.Td ta="right">
        <CountLink count={funnel.leads} label={`${leadsLabel} ${of}`} onClick={() => open()} />
      </Table.Td>
      <Table.Td ta="right">
        <CountLink
          count={funnel.registered}
          label={`${registeredLabel} ${of}`}
          rate={percent(funnel.registered, funnel.leads)}
          onClick={() => open('registered')}
        />
      </Table.Td>
      <Table.Td ta="right" style={edge ? { paddingRight: 0 } : undefined}>
        <CountLink
          count={funnel.booked}
          label={`${bookedLabel} ${of}`}
          rate={percent(funnel.booked, funnel.leads)}
          onClick={() => open('booked')}
        />
      </Table.Td>
    </>
  )
}

/**
 * What an SMS/email campaign moved: the leads who got there after the send, out of those who
 * hadn't yet. When none of the reached leads could still get there, says so instead of 0%.
 */
function SinceSendCell({
  count,
  of,
  reached,
  label,
  done,
  onClick,
}: {
  count: number
  /** The reached leads who hadn't got there at launch. */
  of: number
  reached: number
  label: string
  /** What to say when every reached lead was already there, e.g. "All already registered". */
  done: string
  onClick: () => void
}) {
  return (
    <Table.Td ta="right">
      {of === 0 ? (
        <span className="text-[13px] text-[#6B7280]">{reached ? done : '—'}</span>
      ) : (
        <CountLink count={count} label={label} rate={`of ${of}`} onClick={onClick} />
      )}
    </Table.Td>
  )
}

/**
 * One panel of the report, drawn as the campaign dialog's sections: a heading, what it counts,
 * and its table or empty note.
 */
function ReportCard({
  title,
  badge,
  tools,
  description,
  empty,
  className = '',
  children,
}: {
  title: string
  /** Added to the panel's own classes, e.g. how it fills its column. */
  className?: string
  badge?: ReactNode
  /** Sits at the end of the heading row, e.g. a search. */
  tools?: ReactNode
  description?: string
  /** Shown instead of the table when there is nothing to count. */
  empty?: string
  children: ReactNode
}) {
  return (
    <section
      aria-label={title}
      className={`flex min-w-0 flex-col gap-4 rounded-[14px] border border-[#E7E9EC] p-5 ${className}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-[640px]">
          <h2 className="flex flex-wrap items-center gap-2 text-base font-bold text-[#1F2733]">
            {title}
            {badge}
          </h2>
          {description && <p className="mt-1 text-sm text-[#6B7280]">{description}</p>}
        </div>
        {tools}
      </div>
      {empty ? (
        <p className="rounded-xl bg-[#F6F7F9] px-4 py-6 text-center text-sm text-[#6B7280]">
          {empty}
        </p>
      ) : (
        children
      )}
    </section>
  )
}

const th = (label: string, numeric = false, style?: CSSProperties) => (
  <Table.Th ta={numeric ? 'right' : undefined} className={tableHeaderClass} style={style}>
    {label}
  </Table.Th>
)

/** Narrows a campaign table by name. */
function CampaignSearch({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string
  placeholder: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <CrmSearchInput
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className="w-[240px] max-w-full"
    />
  )
}

/**
 * A campaign table, scrolling under its header, with how many rows it shows. Side by side with
 * the summary it fills its panel's height; stacked (narrow screens) it scrolls past 300px.
 */
function CampaignTable({
  label,
  shown,
  total,
  minWidth,
  children,
}: {
  label: string
  shown: number
  total: number
  minWidth: number
  children: ReactNode
}) {
  return (
    <>
      <div className="max-h-[300px] overflow-auto lg:max-h-none lg:min-h-0 lg:flex-1">
        <Table horizontalSpacing="sm" stickyHeader miw={minWidth} aria-label={label}>
          {children}
        </Table>
      </div>
      <p className="text-xs text-[#6B7280]">
        {shown === total ? total : `${shown} of ${total}`} {total === 1 ? 'campaign' : 'campaigns'}
      </p>
    </>
  )
}

/** The row a campaign table shows when its search matches nothing. */
const noMatchRow = (search: string, columns: number) => (
  <Table.Tr>
    <Table.Td colSpan={columns} className="py-8 text-center text-sm text-[#6B7280]">
      No campaigns match “{search.trim()}”.
    </Table.Td>
  </Table.Tr>
)

const rowClass =
  'h-[52px] border-b border-[#F0F1F3] text-sm text-[#4A5160] transition-colors last:border-b-0 hover:bg-[#FAFBFC]'

type AdCampaignRow = LeadReport['adCampaigns'][number]
type OutreachRow = LeadReport['outreach'][number]

/** A campaign's name in its table row: bold, and the keyboard's way into the campaign's dialog. */
/** A change in a count, signed: +2, −3, or — for none. */
const signed = (n: number) => (n > 0 ? `+${n}` : n < 0 ? `−${-n}` : '—')

/**
 * Where a campaign's leads are, status by status — for an SMS/email campaign also where they were
 * when it was sent, and the change — each count now opening those leads.
 */
function StatusBreakdownSection({
  input,
  delivered,
  of,
  onOpen,
}: {
  input: Parameters<typeof useLeadStatusBreakdown>[0]
  /** An SMS/email campaign's messages out so far: the counts reload as it grows. */
  delivered?: number
  /** Whose leads, for the buttons' labels, e.g. "from Google Ads · winter". */
  of: string
  onOpen: (status: CrmLeadStatus) => void
}) {
  const breakdown = useLeadStatusBreakdown(input, delivered)
  const data = breakdown.data
  const atLaunch = data?.atLaunch
  const total = data?.now.reduce((sum, row) => sum + row.count, 0) ?? 0
  return (
    <CrmSection title={atLaunch ? 'Status of the leads it reached' : 'Where its leads are now'}>
      {breakdown.isError ? (
        <Alert color="red" variant="light" title="Couldn’t load the statuses">
          <Button size="xs" variant="light" color="red" onClick={() => breakdown.refetch()}>
            Try again
          </Button>
        </Alert>
      ) : !data ? (
        <div className="flex justify-center py-6">
          <Loader size="sm" aria-label="Loading the statuses" />
        </div>
      ) : (
        <Table horizontalSpacing="xs" aria-label="Leads by status">
          <Table.Thead>
            <Table.Tr className="h-9 border-b border-[#EAECEF]">
              {th('Status', false, { paddingLeft: 0 })}
              {atLaunch && th('At send', true)}
              {th('Now', true)}
              {th(atLaunch ? 'Change' : 'Share', true, { paddingRight: 0 })}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.now.map((row, i) => {
              const before = atLaunch?.[i].count ?? 0
              return (
                <Table.Tr
                  key={row.status}
                  className="h-10 border-b border-[#F0F1F3] text-sm text-[#4A5160] last:border-b-0"
                >
                  <Table.Td style={{ paddingLeft: 0 }}>
                    <LeadStatusBadge status={row.status} />
                  </Table.Td>
                  {atLaunch && (
                    <Table.Td ta="right" className="tabular-nums">
                      {before}
                    </Table.Td>
                  )}
                  <Table.Td ta="right">
                    <CountLink
                      count={row.count}
                      label={`${LEAD_STATUS_LABELS[row.status]} leads ${of}`}
                      onClick={() => onOpen(row.status)}
                    />
                  </Table.Td>
                  <Table.Td ta="right" className="tabular-nums" style={{ paddingRight: 0 }}>
                    {atLaunch ? signed(row.count - before) : percent(row.count, total) || '—'}
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      )}
    </CrmSection>
  )
}

/** An ad campaign at a glance: the leads it brought in, and how far they got. */
function AdCampaignDialog({
  row,
  sourceLeads,
  onClose,
  onOpenLeads,
}: {
  row: AdCampaignRow
  /** Every lead from the campaign's source, tagged or not. */
  sourceLeads: number
  onClose: () => void
  onOpenLeads: (drill: ReportDrill) => void
}) {
  const source = LEAD_SOURCE_LABELS[row.source]
  const of = `from ${source} · ${row.campaign}`
  const open = (milestone?: LeadMilestone) =>
    onOpenLeads({ source: row.source, campaign: row.campaign, milestone })
  return (
    <CrmModal
      opened
      onClose={onClose}
      size={960}
      title={row.campaign}
      icon={
        <CrmIconTile>
          <IconAd size={20} />
        </CrmIconTile>
      }
      badges={<CrmChip color="gray">{source}</CrmChip>}
      subtitle="Ad campaign"
    >
      <div className="grid items-start gap-5 md:grid-cols-2">
        <CrmSection title="Leads it brought in">
          <div role="group" aria-label="Funnel" className="flex flex-col gap-[18px]">
            <FunnelStep
              label="Leads"
              value={row.leads}
              share={1}
              buttonLabel={`leads ${of}`}
              onClick={() => open()}
            />
            <FunnelStep
              label={`Registered · ${percent(row.registered, row.leads)} of its leads`}
              value={row.registered}
              share={row.leads ? row.registered / row.leads : 0}
              color={LEAD_STATUS_COLORS.registered}
              buttonLabel={`${registeredLabel} ${of}`}
              onClick={() => open('registered')}
            />
            <FunnelStep
              label={`Booked or seen · ${percent(row.booked, row.leads)} of its leads`}
              value={row.booked}
              share={row.leads ? row.booked / row.leads : 0}
              color={LEAD_STATUS_COLORS.scheduled}
              buttonLabel={`${bookedLabel} ${of}`}
              onClick={() => open('booked')}
            />
          </div>
          <p className="text-sm text-[#6B7280]">
            {row.leads} of the {sourceLeads} leads from {source}
          </p>
        </CrmSection>
        <StatusBreakdownSection
          input={{ source: row.source, campaign: row.campaign }}
          of={of}
          onOpen={(status) => onOpenLeads({ source: row.source, campaign: row.campaign, status })}
        />
      </div>
    </CrmModal>
  )
}

/**
 * An SMS/email campaign at a glance: who sent it and when, where the leads it reached stand now
 * against when it was sent, and the message itself. What it moved is in the report's own row.
 */
function OutreachCampaignDialog({
  row,
  onClose,
  onOpenLeads,
}: {
  row: OutreachRow
  onClose: () => void
  onOpenLeads: (drill: ReportDrill) => void
}) {
  const campaign = useCrmCampaign(row.campaignId)
  const details = campaign.data
  const sent = row.launchedAt
    ? `Sent ${dateFormat.format(new Date(row.launchedAt))}${details?.launchedByName ? ` by ${details.launchedByName}` : ''}`
    : undefined
  return (
    <CrmModal
      opened
      onClose={onClose}
      size={1080}
      title={row.name}
      icon={
        <CrmIconTile>
          {row.channel === 'both' ? (
            <IconMessages size={20} />
          ) : row.channel === 'email' ? (
            <IconMail size={20} />
          ) : (
            <IconMessageCircle size={20} />
          )}
        </CrmIconTile>
      }
      badges={
        <>
          <CrmChip color="gray">{CHANNEL_LABELS[row.channel]}</CrmChip>
          <CrmChip color="blue">Existing leads</CrmChip>
        </>
      }
      subtitle={sent}
    >
      <StatusBreakdownSection
        input={{ outreach: row.campaignId }}
        delivered={details && details.counts.sent + details.counts.failed + details.counts.skipped}
        of={`reached by ${row.name}`}
        onOpen={(status) =>
          onOpenLeads({ outreach: { id: row.campaignId, name: row.name }, status })
        }
      />
      <CrmSection title="Message">
        {campaign.isError ? (
          <Alert color="red" variant="light" title="Couldn’t load the message">
            <Button size="xs" variant="light" color="red" onClick={() => campaign.refetch()}>
              Try again
            </Button>
          </Alert>
        ) : !details ? (
          <div className="flex justify-center py-6">
            <Loader size="sm" aria-label="Loading the message" />
          </div>
        ) : (
          <MessagePreview
            channel={details.channel}
            subject={details.subject}
            message={details.message}
          />
        )}
      </CrmSection>
    </CrmModal>
  )
}

/**
 * The CRM report (PD-1993). On the left, a summary rail: every lead through the funnel, then by
 * where it first came from. On the right, the campaigns: ad campaigns and the leads they brought
 * in, and our SMS/email campaigns, which only ever reach existing leads. Every count opens its
 * leads.
 */
export function ReportsTab({
  switcher,
  onOpenLeads,
}: {
  switcher: ReactNode
  onOpenLeads: (drill: ReportDrill) => void
}) {
  const report = useCrmLeadReport()
  const { sources = [], adCampaigns = [], outreach = [] } = report.data ?? {}
  const [adSearch, setAdSearch] = useState('')
  const [outreachSearch, setOutreachSearch] = useState('')
  const [openAd, setOpenAd] = useState<AdCampaignRow | null>(null)
  const [openOutreach, setOpenOutreach] = useState<OutreachRow | null>(null)
  const shownAdCampaigns = adCampaigns.filter((row) =>
    matches(adSearch, row.campaign, LEAD_SOURCE_LABELS[row.source]),
  )
  const shownOutreach = outreach.filter((row) =>
    matches(outreachSearch, row.name, CHANNEL_LABELS[row.channel]),
  )
  const totals = sources.reduce(
    (sum, row) => ({
      leads: sum.leads + row.leads,
      registered: sum.registered + row.registered,
      booked: sum.booked + row.booked,
    }),
    { leads: 0, registered: 0, booked: 0 },
  )
  const share = (part: number) => (totals.leads ? part / totals.leads : 0)

  return (
    <CrmCard
      icon={<IconChartBar size={22} style={{ color: CORAL }} stroke={2} />}
      switcher={switcher}
      // Fills the window under the app bar (as the Shell measures it, less this page's padding),
      // so only the campaign tables scroll; the summary column sets the floor on a short window.
      className="lg:min-h-[calc(100vh-64px-var(--recent-bar-height,0px)-2rem)]"
    >
      {report.isError ? (
        <Alert color="red" variant="light" title="Couldn’t load the report">
          <Button size="xs" variant="light" color="red" onClick={() => report.refetch()}>
            Try again
          </Button>
        </Alert>
      ) : report.isPending ? (
        <div className="flex justify-center py-16">
          <Loader size="sm" aria-label="Loading the report" />
        </div>
      ) : sources.length === 0 ? (
        <p className="py-16 text-center text-sm text-[#6B7280]">
          No leads yet, so nothing to report. Leads appear when someone confirms their phone at
          registration, books through Zocdoc, or is added by staff.
        </p>
      ) : (
        <div className="flex flex-wrap items-start gap-4 pb-2 lg:flex-1 lg:flex-nowrap lg:items-stretch">
          <div className="flex max-w-full min-w-0 flex-[0_0_420px] flex-col gap-4">
            <ReportCard title="All leads">
              <div role="group" aria-label="Totals" className="flex flex-col gap-[18px]">
                <FunnelStep
                  label="Leads · all sources"
                  value={totals.leads}
                  share={1}
                  buttonLabel="leads"
                  onClick={() => onOpenLeads({})}
                />
                <FunnelStep
                  label={`Registered · ${percent(totals.registered, totals.leads)} have a patient chart`}
                  value={totals.registered}
                  share={share(totals.registered)}
                  color={LEAD_STATUS_COLORS.registered}
                  buttonLabel={registeredLabel}
                  onClick={() => onOpenLeads({ milestone: 'registered' })}
                />
                <FunnelStep
                  label={`Booked or seen · ${percent(totals.booked, totals.leads)} of leads`}
                  value={totals.booked}
                  share={share(totals.booked)}
                  color={LEAD_STATUS_COLORS.scheduled}
                  buttonLabel={bookedLabel}
                  onClick={() => onOpenLeads({ milestone: 'booked' })}
                />
              </div>
            </ReportCard>

            <ReportCard title="By source" description="Every lead, by where they first came from.">
              <Table horizontalSpacing="xs" aria-label="Leads by source">
                <Table.Thead>
                  <Table.Tr className="h-10 border-b border-[#EAECEF]">
                    {th('Source', false, { paddingLeft: 0 })}
                    {th('Leads', true)}
                    {th('Registered', true)}
                    {th('Booked', true, { paddingRight: 0 })}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sources.map((row) => (
                    <Table.Tr key={row.source} className={rowClass}>
                      <Table.Td className="font-bold text-[#1F2733]" style={{ paddingLeft: 0 }}>
                        {LEAD_SOURCE_LABELS[row.source]}
                      </Table.Td>
                      <FunnelCells
                        funnel={row}
                        leadsLabel="leads"
                        of={`from ${LEAD_SOURCE_LABELS[row.source]}`}
                        edge
                        open={(milestone) => onOpenLeads({ source: row.source, milestone })}
                      />
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ReportCard>
          </div>

          {/* Out of the flow when side by side, so the summary column alone sets the height. */}
          <div className="relative min-w-0 flex-[1_1_560px]">
            <div className="flex flex-col gap-4 lg:absolute lg:inset-0">
              <ReportCard
                className="lg:max-h-[50%] lg:min-h-0 lg:flex-none"
                title="Ad campaigns"
                badge={adCampaigns.length > 0 && <CountChip>{adCampaigns.length}</CountChip>}
                tools={
                  adCampaigns.length > 0 && (
                    <CampaignSearch
                      label="Search ad campaigns"
                      placeholder="Search ad campaigns"
                      value={adSearch}
                      onChange={setAdSearch}
                    />
                  )
                }
                description="Each tagged ad campaign (utm_campaign) and the leads it brought into the CRM."
                empty={
                  adCampaigns.length === 0
                    ? 'No ad campaigns yet. One appears when someone arrives from an ad tagged with a utm_campaign.'
                    : undefined
                }
              >
                <CampaignTable
                  label="Ad campaigns"
                  shown={shownAdCampaigns.length}
                  total={adCampaigns.length}
                  minWidth={560}
                >
                  <Table.Thead>
                    <Table.Tr className="h-10 border-b border-[#EAECEF]">
                      {th('Campaign')}
                      {th('Source')}
                      {th('Leads', true)}
                      {th('Registered', true)}
                      {th('Booked or seen', true)}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {shownAdCampaigns.length === 0 && noMatchRow(adSearch, 5)}
                    {shownAdCampaigns.map((row) => {
                      const name = `${LEAD_SOURCE_LABELS[row.source]} · ${row.campaign}`
                      return (
                        <Table.Tr
                          key={`${row.source}:${row.campaign}`}
                          className={`${rowClass} cursor-pointer`}
                          onClick={() => setOpenAd(row)}
                        >
                          <Table.Td>
                            <NameButton name={row.campaign} onClick={() => setOpenAd(row)} />
                          </Table.Td>
                          <Table.Td>{LEAD_SOURCE_LABELS[row.source]}</Table.Td>
                          <FunnelCells
                            funnel={row}
                            leadsLabel="leads"
                            of={`from ${name}`}
                            open={(milestone) =>
                              onOpenLeads({ source: row.source, campaign: row.campaign, milestone })
                            }
                          />
                        </Table.Tr>
                      )
                    })}
                  </Table.Tbody>
                </CampaignTable>
              </ReportCard>

              <ReportCard
                className="lg:min-h-0 lg:flex-1"
                title="SMS & email campaigns"
                badge={
                  <>
                    <CrmChip color="blue">Existing leads</CrmChip>
                    {outreach.length > 0 && <CountChip>{outreach.length}</CountChip>}
                  </>
                }
                tools={
                  outreach.length > 0 && (
                    <CampaignSearch
                      label="Search SMS and email campaigns"
                      placeholder="Search SMS & email"
                      value={outreachSearch}
                      onChange={setOutreachSearch}
                    />
                  )
                }
                description="Our own campaigns, sent to leads already in the CRM. Registered and booked count the leads who got there after the send, out of the leads who hadn’t yet."
                empty={outreach.length === 0 ? 'No campaigns launched yet.' : undefined}
              >
                <CampaignTable
                  label="SMS and email campaigns"
                  shown={shownOutreach.length}
                  total={outreach.length}
                  minWidth={640}
                >
                  <Table.Thead>
                    <Table.Tr className="h-10 border-b border-[#EAECEF]">
                      {th('Campaign')}
                      {th('Channel')}
                      {th('Sent')}
                      {th('Reached', true)}
                      {th('Registered since', true)}
                      {th('Booked or seen since', true)}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {shownOutreach.length === 0 && noMatchRow(outreachSearch, 6)}
                    {shownOutreach.map((row) => {
                      const campaign = { id: row.campaignId, name: row.name }
                      return (
                        <Table.Tr
                          key={row.campaignId}
                          className={`${rowClass} cursor-pointer`}
                          onClick={() => setOpenOutreach(row)}
                        >
                          <Table.Td>
                            <NameButton name={row.name} onClick={() => setOpenOutreach(row)} />
                          </Table.Td>
                          <Table.Td>{CHANNEL_LABELS[row.channel]}</Table.Td>
                          <Table.Td className="whitespace-nowrap">
                            {row.launchedAt ? dateFormat.format(new Date(row.launchedAt)) : '—'}
                          </Table.Td>
                          <Table.Td ta="right">
                            <CountLink
                              count={row.reached}
                              label={`leads reached by ${row.name}`}
                              onClick={() => onOpenLeads({ outreach: campaign })}
                            />
                          </Table.Td>
                          <SinceSendCell
                            count={row.registered}
                            of={row.registerable}
                            reached={row.reached}
                            label={`${registeredLabel} since ${row.name}`}
                            done="All already registered"
                            onClick={() =>
                              onOpenLeads({ outreach: campaign, milestone: 'registered' })
                            }
                          />
                          <SinceSendCell
                            count={row.booked}
                            of={row.bookable}
                            reached={row.reached}
                            label={`${bookedLabel} since ${row.name}`}
                            done="All already booked"
                            onClick={() => onOpenLeads({ outreach: campaign, milestone: 'booked' })}
                          />
                        </Table.Tr>
                      )
                    })}
                  </Table.Tbody>
                </CampaignTable>
              </ReportCard>
            </div>
          </div>
        </div>
      )}
      {openAd && (
        <AdCampaignDialog
          row={openAd}
          sourceLeads={sources.find((source) => source.source === openAd.source)?.leads ?? 0}
          onClose={() => setOpenAd(null)}
          onOpenLeads={onOpenLeads}
        />
      )}
      {openOutreach && (
        <OutreachCampaignDialog
          row={openOutreach}
          onClose={() => setOpenOutreach(null)}
          onOpenLeads={onOpenLeads}
        />
      )}
    </CrmCard>
  )
}
